/**
 * EngagementManager.jsx
 * 
 * Main UI for Phase 1 - Engagement Lifecycle Management
 * Features:
 * - Create new engagements
 * - View engagement list
 * - 6-phase status tracking
 * - Phase transitions (Manager role)
 */

import React, { useState, useEffect } from 'react';
import './EngagementManager.css';

const API_BASE = '/api/v1/engagement';

// 6-Phase lifecycle
const PHASES = [
  { id: 'INITIALIZATION', label: 'Initialization', icon: '⚙️', description: 'Setup & team assignment' },
  { id: 'INTAKE', label: 'Intake', icon: '📥', description: 'Document upload & parsing' },
  { id: 'PLANNING', label: 'Planning', icon: '📋', description: 'Control mapping & risk assessment' },
  { id: 'FIELDWORK', label: 'Fieldwork', icon: '🔍', description: 'Testing & evidence collection' },
  { id: 'REPORTING', label: 'Reporting', icon: '📊', description: 'Report generation' },
  { id: 'CLOSED', label: 'Closed', icon: '✅', description: 'Archived & read-only' },
];

const VALID_TRANSITIONS = {
  'INITIALIZATION': ['INTAKE'],
  'INTAKE': ['PLANNING'],
  'PLANNING': ['FIELDWORK'],
  'FIELDWORK': ['REPORTING'],
  'REPORTING': ['CLOSED'],
  'CLOSED': ['REPORTING'], // Un-archive
};

const EngagementManager = ({ userInfo, onBack, onSelectEngagement }) => {
  const [engagements, setEngagements] = useState([]);
  const [selectedEngagement, setSelectedEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // User's firm and clients
  const [userFirm, setUserFirm] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  
  // Add client form
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', code: '', industry_code: '' });
  
  const [newEngagement, setNewEngagement] = useState({
    name: '',
    period_start: '',
    period_end: '',
    due_date: '',
    description: '',
  });

  useEffect(() => {
    loadEngagements();
    loadUserFirm();
  }, []);

  const loadUserFirm = async () => {
    try {
      const response = await fetch(`${API_BASE}/users/me/firm`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setUserFirm(data.firm);
        // Load clients for this firm
        loadClients(data.firm.id);
      } else {
        console.error('No firm assigned to user');
      }
    } catch (err) {
      console.error('Error loading user firm:', err);
    }
  };

  const loadClients = async (firmId) => {
    try {
      const response = await fetch(`${API_BASE}/firms/${firmId}/clients`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data.clients || []);
      }
    } catch (err) {
      console.error('Error loading clients:', err);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!userFirm) {
      setError('No firm assigned. Cannot create client.');
      return;
    }
    
    try {
      const params = new URLSearchParams({
        name: newClient.name,
        ...(newClient.code && { code: newClient.code }),
        ...(newClient.industry_code && { industry_code: newClient.industry_code }),
      });
      
      const response = await fetch(`${API_BASE}/firms/${userFirm.id}/clients?${params}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setClients([...clients, data.client]);
        setSelectedClientId(data.client.id);
        setShowAddClient(false);
        setNewClient({ name: '', code: '', industry_code: '' });
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to create client');
      }
    } catch (err) {
      setError('Error creating client: ' + err.message);
    }
  };

  const loadEngagements = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/engagements?module_scope=soc2`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setEngagements(data.engagements || []);
      } else {
        // Demo data if API not available
        setEngagements([
          {
            id: 'demo-1',
            client_name: 'Acme Corp',
            engagement_name: 'SOC 2 Type II 2025',
            status: 'INITIALIZATION',
            fiscal_year_end: '2025-12-31',
            created_at: new Date().toISOString(),
          },
        ]);
      }
    } catch (err) {
      console.error('Error loading engagements:', err);
      // Use demo data
      setEngagements([
        {
          id: 'demo-1',
          client_name: 'Acme Corp',
          engagement_name: 'SOC 2 Type II 2025',
          status: 'INITIALIZATION',
          fiscal_year_end: '2025-12-31',
          created_at: new Date().toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEngagement = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validate firm and client
    if (!userFirm) {
      setError('No firm assigned. Contact administrator.');
      return;
    }
    if (!selectedClientId) {
      setError('Please select a client or create a new one.');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/engagements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
        body: JSON.stringify({
          name: newEngagement.name,
          period_start: newEngagement.period_start,
          period_end: newEngagement.period_end,
          due_date: newEngagement.due_date || null,
          description: newEngagement.description || null,
          type: 'SOC2_TYPE2',
          selected_tsc_criteria: ['CC'],
          firm_id: userFirm.id,
          client_id: selectedClientId,
          settings: {
            module: 'soc2',
            created_via: 'engagement_manager',
          },
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setEngagements([data.engagement, ...engagements]);
        setShowCreateForm(false);
        setNewEngagement({ name: '', period_start: '', period_end: '', due_date: '', description: '' });
        setSelectedClientId('');
      } else {
        const err = await response.json();
        // Handle Pydantic validation errors (array of objects)
        if (Array.isArray(err.detail)) {
          setError(err.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', '));
        } else if (typeof err.detail === 'object') {
          setError(JSON.stringify(err.detail));
        } else {
          setError(err.detail || 'Failed to create engagement');
        }
      }
    } catch (err) {
      // Demo mode - add locally
      const demoEngagement = {
        id: `demo-${Date.now()}`,
        ...newEngagement,
        status: 'INITIALIZATION',
        created_at: new Date().toISOString(),
      };
      setEngagements([demoEngagement, ...engagements]);
      setShowCreateForm(false);
      setNewEngagement({ name: '', period_start: '', period_end: '', due_date: '', description: '' });
    }
  };

  const handleTransition = async (engagementId, toStatus) => {
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE}/engagements/${engagementId}/transition`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
        body: JSON.stringify({
          to_status: toStatus,
          reason: null,
        }),
      });
      
      if (response.ok) {
        loadEngagements();
        if (selectedEngagement?.id === engagementId) {
          setSelectedEngagement({ ...selectedEngagement, status: toStatus });
        }
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to transition engagement');
      }
    } catch (err) {
      // Demo mode - update locally
      setEngagements(engagements.map(e => 
        e.id === engagementId ? { ...e, status: toStatus } : e
      ));
      if (selectedEngagement?.id === engagementId) {
        setSelectedEngagement({ ...selectedEngagement, status: toStatus });
      }
    }
  };

  const getPhaseIndex = (status) => {
    return PHASES.findIndex(p => p.id === status);
  };

  const canTransition = (fromStatus, toStatus) => {
    return VALID_TRANSITIONS[fromStatus]?.includes(toStatus);
  };

  const getOpenFeatureForStatus = (status) => {
    switch ((status || '').toUpperCase()) {
      case 'PLANNING':
        return 'planning';
      case 'INTAKE':
      case 'INITIALIZATION':
      default:
        return 'engagement-detail';
    }
  };

  const renderPhaseProgress = (currentStatus) => {
    const currentIndex = getPhaseIndex(currentStatus);
    
    return (
      <div className="phase-progress">
        {PHASES.map((phase, idx) => (
          <React.Fragment key={phase.id}>
            <div 
              className={`phase-step ${idx < currentIndex ? 'completed' : ''} ${idx === currentIndex ? 'current' : ''}`}
              title={phase.description}
            >
              <span className="phase-icon">{phase.icon}</span>
              <span className="phase-label">{phase.label}</span>
            </div>
            {idx < PHASES.length - 1 && (
              <div className={`phase-connector ${idx < currentIndex ? 'completed' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderEngagementCard = (engagement) => {
    const currentIndex = getPhaseIndex(engagement.status);
    const nextPhase = PHASES[currentIndex + 1];
    
    return (
      <div 
        key={engagement.id} 
        className={`engagement-card ${selectedEngagement?.id === engagement.id ? 'selected' : ''}`}
        onClick={() => setSelectedEngagement(engagement)}
      >
        <div className="card-header">
          <h3>{engagement.name || engagement.engagement_name}</h3>
          <span className={`status-badge status-${(engagement.status || 'INITIALIZATION').toLowerCase()}`}>
            {engagement.status || 'INITIALIZATION'}
          </span>
        </div>
        
        <p className="engagement-name">{engagement.client_name || engagement.description || ''}</p>
        
        <div className="card-details">
          <span>📅 Period: {engagement.period_start || 'Not set'} - {engagement.period_end || ''}</span>
          {engagement.due_date && <span>⏰ Due: {engagement.due_date}</span>}
        </div>
        
        {renderPhaseProgress(engagement.status)}
        
        <div className="card-actions">
          {nextPhase && canTransition(engagement.status, nextPhase.id) && (
            <button 
              className="btn-transition"
              onClick={(e) => {
                e.stopPropagation();
                handleTransition(engagement.id, nextPhase.id);
              }}
              title={`Requires Manager role`}
            >
              Move to {nextPhase.label} →
            </button>
          )}
          
          <button 
            className="btn-open"
            onClick={(e) => {
              e.stopPropagation();
              const targetFeature = getOpenFeatureForStatus(engagement.status);
              onSelectEngagement?.(engagement, targetFeature);
            }}
          >
            Open Engagement
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="engagement-manager">
      {/* Header */}
      <div className="manager-header">
        <div className="header-left">
          <button className="back-button" onClick={onBack}>
            ← Back to Dashboard
          </button>
          <div>
            <h1>Engagement Manager</h1>
            <p>Phase 1 - 6-Phase Lifecycle Management</p>
          </div>
        </div>
        <div className="header-right">
          <button 
            className="btn-create"
            onClick={() => setShowCreateForm(true)}
          >
            + New Engagement
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Create Form Modal */}
      {showCreateForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Create New Engagement</h2>
            
            {/* Show user's firm */}
            {userFirm && (
              <div className="firm-info">
                <span>Firm: <strong>{userFirm.name}</strong> ({userFirm.code})</span>
              </div>
            )}
            {!userFirm && (
              <div className="error-banner">
                <span>⚠️ No firm assigned. Contact administrator.</span>
              </div>
            )}
            
            <form onSubmit={handleCreateEngagement}>
              {/* Client Selection */}
              <div className="form-group">
                <label>Client *</label>
                <div className="client-select-row">
                  <select
                    value={selectedClientId}
                    onChange={(e) => setSelectedClientId(e.target.value)}
                    required
                    disabled={!userFirm}
                  >
                    <option value="">Select a client...</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name} {client.code ? `(${client.code})` : ''}
                      </option>
                    ))}
                  </select>
                  <button 
                    type="button" 
                    className="btn-add-client"
                    onClick={() => setShowAddClient(!showAddClient)}
                    disabled={!userFirm}
                  >
                    {showAddClient ? 'Cancel' : '+ New Client'}
                  </button>
                </div>
              </div>
              
              {/* Add Client Form (inline) */}
              {showAddClient && (
                <div className="add-client-form">
                  <div className="form-group">
                    <label>Client Name *</label>
                    <input
                      type="text"
                      value={newClient.name}
                      onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                      placeholder="e.g., Acme Corporation"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Code (optional)</label>
                      <input
                        type="text"
                        value={newClient.code}
                        onChange={(e) => setNewClient({ ...newClient, code: e.target.value })}
                        placeholder="e.g., ACME"
                      />
                    </div>
                    <div className="form-group">
                      <label>Industry (optional)</label>
                      <input
                        type="text"
                        value={newClient.industry_code}
                        onChange={(e) => setNewClient({ ...newClient, industry_code: e.target.value })}
                        placeholder="e.g., 541211"
                      />
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn-save-client"
                    onClick={handleCreateClient}
                    disabled={!newClient.name}
                  >
                    Save Client
                  </button>
                </div>
              )}
              
              <div className="form-group">
                <label>Engagement Name *</label>
                <input
                  type="text"
                  value={newEngagement.name}
                  onChange={(e) => setNewEngagement({ ...newEngagement, name: e.target.value })}
                  required
                  placeholder="e.g., SOC 2 Type II 2025"
                />
              </div>
              
              <div className="form-group">
                <label>Period Start *</label>
                <input
                  type="date"
                  value={newEngagement.period_start}
                  onChange={(e) => setNewEngagement({ ...newEngagement, period_start: e.target.value })}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Period End *</label>
                <input
                  type="date"
                  value={newEngagement.period_end}
                  onChange={(e) => setNewEngagement({ ...newEngagement, period_end: e.target.value })}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Due Date</label>
                <input
                  type="date"
                  value={newEngagement.due_date}
                  onChange={(e) => setNewEngagement({ ...newEngagement, due_date: e.target.value })}
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={newEngagement.description}
                  onChange={(e) => setNewEngagement({ ...newEngagement, description: e.target.value })}
                  placeholder="Optional description"
                />
              </div>
              
              <div className="form-actions">
                <button type="button" onClick={() => setShowCreateForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Create Engagement</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="manager-content">
        {loading ? (
          <div className="loading">Loading engagements...</div>
        ) : engagements.length === 0 ? (
          <div className="empty-state">
            <h3>No Engagements Yet</h3>
            <p>Create your first engagement to get started.</p>
            <button className="btn-create" onClick={() => setShowCreateForm(true)}>
              + Create Engagement
            </button>
          </div>
        ) : (
          <div className="engagements-grid">
            {engagements.map(renderEngagementCard)}
          </div>
        )}
      </div>

      {/* Phase Legend */}
      <div className="phase-legend">
        <h4>Engagement Phases</h4>
        <div className="legend-items">
          {PHASES.map(phase => (
            <div key={phase.id} className="legend-item">
              <span className="phase-icon">{phase.icon}</span>
              <span className="phase-name">{phase.label}</span>
              <span className="phase-desc">{phase.description}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EngagementManager;
